import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { accessToken } from '../../../../configuration/tokenHandling/accessToken';
import type { TermedLevel } from '../../common/termedLevel/types';
import { getMarketplaceBackend } from '../../config/pathURL/config';
import { decodeJson, transformRtkQueryError } from '../api';
import { ApiContractsCodec } from './contract.types';
import { mapApiContracts } from './contractMapper';

export interface FetchContractsOptions {
    paymentMethodRequired?: boolean;
}

export interface UpdateContractData {
    productId: string;
    resourceIds: string[];
    renewsToLevel?: string;
}

export const contractApiSlice = createApi({
    reducerPath: 'contractApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getMarketplaceBackend(),
        prepareHeaders: headers => {
            const token = accessToken.getAccessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Contracts'],
    endpoints: builder => ({
        getContracts: builder.query<TermedLevel[], FetchContractsOptions | undefined>({
            query: options => ({
                url: '/contracts',
                params: options?.paymentMethodRequired ? { payment_method_required: true } : undefined,
            }),
            transformResponse: (response: unknown) => mapApiContracts()(decodeJson(ApiContractsCodec)(response)),
            transformErrorResponse: response => transformRtkQueryError(response, [401]),
            providesTags: ['Contracts'],
        }),
        updateContract: builder.mutation<null, UpdateContractData>({
            query: ({ productId, resourceIds, renewsToLevel }) => ({
                url: `/contracts/${productId}/config`,
                method: 'POST',
                body: {
                    resource_ids: resourceIds,
                    renews_to_level: renewsToLevel,
                },
            }),
            transformResponse: () => null,
            transformErrorResponse: response => transformRtkQueryError(response),
            invalidatesTags: (_result, error) => (error ? [] : ['Contracts']),
        }),
    }),
});

export const { useGetContractsQuery } = contractApiSlice;
